import { useState } from 'react';
import {
  ChevronDown,
  ChevronRight,
  Wrench,
  Code,
  ExternalLink,
  FileText,
  Tag,
  Layers,
  Clock,
  Globe,
  Glasses,
} from 'lucide-react';
import { SeverityBadge, type Severity } from './SeverityBadge';
import { CodeSnippet, ElementSelector } from './CodeSnippet';
import { TimeEstimateInput } from './TimeEstimateInput';
import { IssueCategory } from '../../types';

export type WcagPrinciple = 'perceivable' | 'operable' | 'understandable' | 'robust';
export type ToolSource = 'AXE' | 'LIGHTHOUSE' | 'PA11Y' | 'LEGACY';

export interface AffectedElement {
  selector: string;
  html: string;
  target?: string[];
  failureSummary?: string;
  pageUrl?: string;
}

export interface IssueCardData {
  ruleId: string;
  title: string;
  description: string;
  severity: Severity;
  wcagCriteria?: string[];
  wcagLevel?: string;
  wcagPrinciple?: WcagPrinciple;
  toolSource?: ToolSource;
  affectedElements?: AffectedElement[];
  remediation?: string;
  helpUrl?: string;
  pageUrl?: string;
  affectedPages?: string[];
  count?: number;
  estimatedTimeToFix?: number;
  /** Unverifiable indicator, shown as "Manual review needed" instead of a severity */
  needsManualReview?: boolean;
}

interface TimeEstimateConfig {
  scanId: string;
  pageUrl: string;
  issueType: IssueCategory;
  issueIdentifier: string;
  onUpdate: (minutes: number | null) => Promise<void>;
}

interface IssueCardProps {
  issue: IssueCardData;
  defaultExpanded?: boolean;
  showPageUrl?: boolean;
  timeEstimateConfig?: TimeEstimateConfig;
  /** When set, a "Mark as reviewed" action is shown for manual-review items */
  onMarkReviewed?: () => void;
  /**
   * When set, the card carries a tick for "I checked this myself". Used by the link
   * checks, where the answer is a fact about this scan (the link opens or it does
   * not) rather than a decision about the site — so the reader can settle it here,
   * on the page where they read the finding, instead of hunting for the right tab.
   */
  onToggleChecked?: (checked: boolean) => void;
  checked?: boolean;
}

const WCAG_PRINCIPLE_LABELS: Record<WcagPrinciple, { label: string; short: string }> = {
  perceivable: { label: 'Perceivable', short: 'P' },
  operable: { label: 'Operable', short: 'O' },
  understandable: { label: 'Understandable', short: 'U' },
  robust: { label: 'Robust', short: 'R' },
};

const TOOL_SOURCE_LABELS: Record<ToolSource, { label: string; color: string }> = {
  AXE: { label: 'axe-core', color: 'bg-purple-100 text-purple-700' },
  LIGHTHOUSE: { label: 'Lighthouse', color: 'bg-blue-100 text-blue-700' },
  PA11Y: { label: 'Pa11y', color: 'bg-green-100 text-green-700' },
  LEGACY: { label: 'Legacy', color: 'bg-gray-100 text-gray-600' },
};

export function IssueCard({
  issue,
  defaultExpanded = false,
  showPageUrl = false,
  timeEstimateConfig,
  onMarkReviewed,
  onToggleChecked,
  checked = false,
}: IssueCardProps) {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);
  const [localTimeEstimate, setLocalTimeEstimate] = useState<number | undefined>(
    issue.estimatedTimeToFix
  );

  const handleTimeEstimateChange = async (minutes: number | null) => {
    if (timeEstimateConfig) {
      await timeEstimateConfig.onUpdate(minutes);
      setLocalTimeEstimate(minutes ?? undefined);
    }
  };

  const realElements = issue.affectedElements?.filter(
    (el) => !el.html || !/^<[a-z][a-z0-9]*(-[a-z0-9]+)+>$/.test(el.html.trim())
  );
  const affectedCount = realElements?.length || issue.count || 0;

  return (
    <div
      className={`border rounded-lg overflow-hidden ${
        checked ? 'border-status-success/30 bg-status-success/5' : 'border-gray-light bg-white'
      }`}
    >
      {/* Header - clickable to expand/collapse */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-start gap-4 p-4 hover:bg-gray-light/30 transition-colors text-left"
        aria-expanded={isExpanded}
      >
        <div className="flex-1 min-w-0">
          {/* Badges row */}
          <div className="flex flex-wrap items-center gap-2 mb-2">
            <span className="text-body-sm font-mono text-coral bg-coral/10 px-2 py-0.5 rounded">
              {issue.ruleId}
            </span>
            {issue.needsManualReview && checked ? (
              <span className="inline-flex items-center gap-1.5 px-3 py-0.5 rounded-full text-caption font-medium bg-status-success/10 text-status-success border border-status-success/30">
                <Glasses size={14} />
                Checked
              </span>
            ) : issue.needsManualReview ? (
              <span className="inline-flex items-center gap-1.5 px-3 py-0.5 rounded-full text-caption font-medium bg-gray-100 text-gray-600 border border-gray-200">
                <Glasses size={14} />
                Manual review needed
              </span>
            ) : (
              <SeverityBadge severity={issue.severity} size="sm" />
            )}
            {issue.wcagCriteria && issue.wcagCriteria.length > 0 && (
              <span className="text-body-sm text-gray-medium flex items-center gap-1">
                <Tag size={12} />
                WCAG {issue.wcagCriteria.join(', ')}
                {issue.wcagLevel && ` (${issue.wcagLevel})`}
              </span>
            )}
            {issue.wcagPrinciple && (
              <span className="text-caption px-2 py-0.5 bg-gray-100 text-gray-600 rounded">
                {WCAG_PRINCIPLE_LABELS[issue.wcagPrinciple].label}
              </span>
            )}
            {issue.toolSource && (
              <span
                className={`text-caption px-2 py-0.5 rounded ${TOOL_SOURCE_LABELS[issue.toolSource].color}`}
              >
                {TOOL_SOURCE_LABELS[issue.toolSource].label}
              </span>
            )}
            {localTimeEstimate && (
              <span className="text-body-sm text-gray-medium flex items-center gap-1">
                <Clock size={12} />
                {localTimeEstimate < 60
                  ? `${localTimeEstimate}m`
                  : `${Math.floor(localTimeEstimate / 60)}h${localTimeEstimate % 60 > 0 ? ` ${localTimeEstimate % 60}m` : ''}`}
              </span>
            )}
          </div>

          {/* Title and description */}
          <h4 className="text-body font-semibold text-dark-text mb-1">{issue.title}</h4>
          <p className="text-body-sm text-gray-medium line-clamp-2">{issue.description}</p>

          {/* Affected elements count */}
          {affectedCount > 0 && (
            <div className="flex items-center gap-2 mt-2 text-body-sm text-gray-medium">
              <Code size={14} />
              <span>
                {affectedCount} affected element{affectedCount !== 1 ? 's' : ''}
              </span>
            </div>
          )}

          {/* Found on X pages (grouped) */}
          {issue.affectedPages && issue.affectedPages.length > 1 ? (
            <div className="flex items-center gap-2 mt-2 text-body-sm text-gray-medium">
              <FileText size={14} />
              <span>Found on {issue.affectedPages.length} pages</span>
            </div>
          ) : (
            /* Page URL (single page) */
            showPageUrl &&
            issue.pageUrl && (
              <div className="flex items-center gap-2 mt-2 text-body-sm text-gray-medium">
                <FileText size={14} />
                <span className="truncate">{issue.pageUrl}</span>
              </div>
            )
          )}
        </div>

        {/* Expand/collapse icon */}
        {isExpanded ? (
          <ChevronDown size={20} className="text-gray-medium flex-shrink-0 mt-1" />
        ) : (
          <ChevronRight size={20} className="text-gray-medium flex-shrink-0 mt-1" />
        )}
      </button>

      {/* Expanded content */}
      {isExpanded && (
        <div className="border-t border-gray-light">
          {/* Manual review action */}
          {issue.needsManualReview && onMarkReviewed && (
            <div className="p-4 border-b border-gray-light">
              <button
                onClick={onMarkReviewed}
                className="inline-flex items-center gap-2 px-3 py-2 text-body-sm text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <Glasses size={16} />
                Mark as reviewed
              </button>
              <p className="text-caption text-gray-medium mt-1">
                Confirm you have checked this manually. Reviewed items are excluded from the results.
              </p>
            </div>
          )}

          {/* Remediation section */}
          {issue.remediation && (
            <div className="p-4 bg-gray-light/20 border-b border-gray-light">
              <div className="flex items-start gap-3">
                <Wrench size={18} className="text-coral flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <h5 className="text-body-sm font-semibold text-dark-text mb-2">How to Fix</h5>
                  <p className="text-body-sm text-gray-medium whitespace-pre-line">
                    {issue.remediation}
                  </p>

                  {/* The answer to a link check is a fact the reader can establish in
                      one click, so it is settled here, right under the instruction
                      that asks for it — not in a separate tab. */}
                  {onToggleChecked && (
                    <label className="mt-3 inline-flex items-center gap-2 cursor-pointer select-none">
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={(e) => onToggleChecked(e.target.checked)}
                        className="w-4 h-4 accent-status-success cursor-pointer"
                      />
                      <span className="text-body-sm text-dark-text">Verified</span>
                    </label>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Help link */}
          {issue.helpUrl && (
            <div className="px-4 py-3 bg-gray-light/10 border-b border-gray-light">
              <a
                href={issue.helpUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-body-sm text-coral hover:text-coral/80"
              >
                <ExternalLink size={14} />
                Learn more about this issue
              </a>
            </div>
          )}

          {/* Affected pages with their elements grouped (grouped mode).
              Render whenever we have pages — even with no element-level detail.
              EAA Chapter-9 clauses are derived from axe and carry affected pages
              but no elements (see deriveClause9FromAxe); those must still list the
              pages, falling back to a plain page list via the allSynthetic path. */}
          {issue.affectedPages &&
            issue.affectedPages.length > 0 &&
            (() => {
              const affectedPages = issue.affectedPages ?? [];
              const affectedElements = issue.affectedElements ?? [];
              const syntheticPattern = /^<[a-z][a-z0-9]*(-[a-z0-9]+)+>$/;
              const realElements = affectedElements.filter(
                (el) => !el.html || !syntheticPattern.test(el.html.trim())
              );

              // Group elements by page
              const byPage = new Map<string, AffectedElement[]>();
              for (const page of affectedPages) {
                byPage.set(page, []);
              }
              for (const el of realElements) {
                const page = el.pageUrl ?? affectedPages[0];
                if (!byPage.has(page)) byPage.set(page, []);
                const pageElements = byPage.get(page);
                if (pageElements) pageElements.push(el);
              }

              const allSynthetic = realElements.length === 0;

              return (
                <div className="p-4 border-b border-gray-light">
                  <div className="flex items-center gap-2 mb-3">
                    <FileText size={16} className="text-gray-medium" />
                    <h5 className="text-body-sm font-semibold text-dark-text">
                      Affected Pages ({affectedPages.length})
                    </h5>
                  </div>
                  {allSynthetic ? (
                    <div className="space-y-1">
                      {affectedPages.map((url, i) => (
                        <p key={i} className="text-body-sm text-gray-medium truncate">
                          {url}
                        </p>
                      ))}
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {Array.from(byPage.entries()).map(([url, elements]) => (
                        <div key={url} className="border-l-2 border-coral/30 pl-3">
                          <div className="flex items-center gap-1 mb-2">
                            <Globe size={12} className="text-gray-medium flex-shrink-0" />
                            <p className="text-body-sm text-dark-text truncate font-medium">
                              {url}
                            </p>
                          </div>
                          {elements.length === 0 ? (
                            <p className="text-body-sm text-gray-medium italic">
                              Document-level check — no specific element.
                            </p>
                          ) : (
                            <div className="space-y-2">
                              {elements.slice(0, 3).map((element, idx) => (
                                <AffectedElementCard key={idx} element={element} index={idx + 1} />
                              ))}
                              {elements.length > 3 && (
                                <p className="text-body-sm text-gray-medium italic">
                                  And {elements.length - 3} more elements on this page...
                                </p>
                              )}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })()}

          {/* Chapter 9 clauses are derived from automated accessibility testing
              (axe-core). Rather than duplicate element-level detail here, point to
              the Accessibility tab, the single source of truth for it (Decision B). */}
          {issue.ruleId?.startsWith('clause-9.') && (
            <div className="p-4 border-b border-gray-light">
              <div className="p-3 bg-blue-500/5 border border-blue-500/20 rounded-lg">
                <p className="text-body-sm text-gray-medium">
                  Detected via automated accessibility testing (axe-core), mapped from{' '}
                  <span className="font-medium text-dark-text">
                    WCAG {issue.ruleId.replace(/^clause-9\./, '')}
                  </span>
                  . For the exact elements and how to fix, see the{' '}
                  <span className="font-medium text-dark-text">Accessibility</span> tab.
                </p>
              </div>
            </div>
          )}

          {/* Affected elements (non-grouped / single page) */}
          {!issue.affectedPages &&
            issue.affectedElements &&
            issue.affectedElements.length > 0 &&
            (() => {
              const affectedElements = issue.affectedElements ?? [];
              const syntheticPattern = /^<[a-z][a-z0-9]*(-[a-z0-9]+)+>$/;
              const real = affectedElements.filter(
                (el) => !el.html || !syntheticPattern.test(el.html.trim())
              );
              const allSynthetic = real.length === 0;
              return (
                <div className="p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <Layers size={16} className="text-gray-medium" />
                    <h5 className="text-body-sm font-semibold text-dark-text">
                      {allSynthetic ? 'Affected Elements' : `Affected Elements (${real.length})`}
                    </h5>
                  </div>
                  {allSynthetic ? (
                    <p className="text-body-sm text-gray-medium italic">
                      Document-level check — no specific HTML element. The violation is reported at
                      the page level, not on a single element.
                    </p>
                  ) : (
                    <div className="space-y-3">
                      {real.slice(0, 5).map((element, index) => (
                        <AffectedElementCard key={index} element={element} index={index + 1} />
                      ))}
                      {real.length > 5 && (
                        <p className="text-body-sm text-gray-medium italic">
                          And {real.length - 5} more elements...
                        </p>
                      )}
                    </div>
                  )}
                </div>
              );
            })()}

          {/* Time Estimate (Admin editable) */}
          {timeEstimateConfig && (
            <div className="px-4 py-3 bg-gray-light/10 border-b border-gray-light">
              <div className="flex items-center gap-3">
                <Clock size={16} className="text-gray-medium flex-shrink-0" />
                <div className="flex-1">
                  <h5 className="text-body-sm font-semibold text-dark-text mb-2">
                    Estimated Time to Fix
                  </h5>
                  <TimeEstimateInput
                    value={localTimeEstimate ?? null}
                    onChange={handleTimeEstimateChange}
                  />
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

interface AffectedElementCardProps {
  element: AffectedElement;
  index: number;
}

// axe-core uses synthetic nodes like <landmark-one-main> for document-level checks
// where there is no real HTML element to show — detect and suppress these.
// Synthetic nodes always have hyphens in the tag name; standard HTML tags never do.
function isAxeSyntheticNode(html: string): boolean {
  return /^<[a-z][a-z0-9]*(-[a-z0-9]+)+>$/.test(html.trim());
}

function AffectedElementCard({ element, index }: AffectedElementCardProps) {
  const [showHtml, setShowHtml] = useState(false);
  const synthetic = element.html ? isAxeSyntheticNode(element.html) : false;
  const hasRealHtml = element.html && !synthetic;

  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden">
      <div className="px-3 py-2 bg-gray-50 flex items-center justify-between">
        <div className="flex items-center gap-2 min-w-0">
          <span className="text-caption text-gray-500 flex-shrink-0">#{index}</span>
          {synthetic ? (
            <span className="text-caption text-gray-500 italic">Document-level check</span>
          ) : (
            <ElementSelector selector={element.selector} />
          )}
        </div>
        {hasRealHtml && (
          <button
            onClick={() => setShowHtml(!showHtml)}
            className="text-caption text-coral hover:text-coral/80 flex-shrink-0 ml-2"
          >
            {showHtml ? 'Hide HTML' : 'Show HTML'}
          </button>
        )}
      </div>

      {element.failureSummary && (
        <div className="px-3 py-2 border-t border-gray-200 bg-white">
          <p className="text-body-sm text-gray-600">{element.failureSummary}</p>
        </div>
      )}

      {showHtml && hasRealHtml && element.html && (
        <div className="border-t border-gray-200">
          <CodeSnippet code={element.html} language="html" maxLines={5} showCopyButton />
        </div>
      )}
    </div>
  );
}

interface IssueCardSkeletonProps {
  count?: number;
}

export function IssueCardSkeleton({ count = 3 }: IssueCardSkeletonProps) {
  return (
    <div className="space-y-4">
      {Array.from({ length: count }).map((_, index) => (
        <div
          key={index}
          className="border border-gray-light rounded-lg overflow-hidden animate-pulse"
        >
          <div className="p-4">
            <div className="flex flex-wrap gap-2 mb-3">
              <div className="h-5 w-24 bg-gray-light rounded" />
              <div className="h-5 w-16 bg-gray-light rounded-full" />
              <div className="h-5 w-20 bg-gray-light rounded" />
            </div>
            <div className="h-5 w-3/4 bg-gray-light rounded mb-2" />
            <div className="h-4 w-full bg-gray-light rounded mb-1" />
            <div className="h-4 w-2/3 bg-gray-light rounded" />
          </div>
        </div>
      ))}
    </div>
  );
}
